import React, { useState, useEffect, useCallback } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper, Box, IconButton, Collapse, Select, MenuItem, FormControl, InputLabel, Checkbox, Popover, TextField, Button } from '@material-ui/core';
import { green, red } from '@material-ui/core/colors';
import { KeyboardArrowDown, KeyboardArrowUp, NoteAdd, RateReview } from '@material-ui/icons';
import _ from 'lodash';
import MultiCurrencyField from '../../../UIComponents/multiCurrencyField';
import CaptionedField from '../../../UIComponents/captionedField';

const COMPARATOR_FIELDS = [

  { value: 'basicSalary', label: 'basicSalary' },
  {value:  'salaryInBasicCurrency', label: 'Basic Salary(in Basic Currency'},
  { value: 'additionalFixedEarnings', label: 'additionalFixedEarnings' },
  { value: 'overtimeTotal', label: 'overtimeTotal' },
  { value: 'empPension', label: 'empPension' },
  { value: 'companyPension', label: 'companyPension' },
  { value: 'incomeTax', label: 'incomeTax' },
  { value: 'totalDeduction', label: 'totalDeduction' },
  { value: 'totalIncome', label: 'totalIncome' },
  { value: 'netPay', label: 'netPay' },
  { value: 'cash', label: 'cash' },
];

function getCurrencyRate(currency, usedCurrencyRates) {
    if (!currency || currency.toLowerCase() === 'etb') return 1;
    return usedCurrencyRates && usedCurrencyRates[currency.toLowerCase()] ? usedCurrencyRates[currency.toLowerCase()] : 1;
}

function getPayrollMap(payrolls) {
    const map = {};
    (payrolls || []).forEach(p => {
        map[p.employeeId] = p;
    });
    return map;
}

function NotesPopover({ open, anchorEl, onClose, onSave, initialValue }) {
    const [note, setNote] = useState(initialValue || '');
    useEffect(() => {
        setNote(initialValue || '');
    }, [initialValue]);

    const handleSave = () => {
        onSave(note);
        onClose();
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Box p={2}>
                <TextField
                    label="Note"
                    multiline
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    variant="outlined"
                    fullWidth
                />
                <Box mt={1} display="flex" justifyContent="flex-end">
                    <Button onClick={onClose} size="small">Cancel</Button>
                    <Button onClick={handleSave} color="primary" size="small">Save</Button>
                </Box>
            </Box>
        </Popover>
    );
}

function comparePayrollItems(current, prev, usedCurrencyRates) {
    const diffs = [];
    const fields = [
        'basicSalary', 'additionalFixedEarnings', 'bonusesTotal', 'overtimeTotal', 'absenceDeduction',
        'empPension', 'companyPension', 'incomeTax', 'totalDeduction', 'totalIncome', 'netPay', 'cash'
    ];
    fields.forEach(field => {
        let currVal = current[field];
        let prevVal = prev[field];
        let ignoreDueToCurrency = false;
        if (field === 'basicSalary' && (current.basicCurrency && current.basicCurrency.toLowerCase() !== 'etb')) {
            const currRate = current.currencyRate
            const prevRate = prev.currencyRate
        
            // console.log({currRate, prevRate})
            if (currRate !== prevRate) {
                const prevValAdj = prevVal * (currRate / prevRate);
                if (Math.abs(currVal - prevValAdj) < 1) {
                    ignoreDueToCurrency = true;
                }
                prevVal = prevValAdj;
                // console.log({currVal, prevVal, currRate, prevRate})
            }
        }
        if (currVal !== undefined && prevVal !== undefined && Math.abs(currVal - prevVal) > 0.01 && !ignoreDueToCurrency) {
            diffs.push({
                field,
                prevVal,
                currVal,
                diff: currVal - prevVal,
            });
        }
    });
    return diffs;
}

function PayrollComparisonTable({ payrollRun, payrollRun1, initialCheckedIds = [], initialNotes = {}, onReviewDataChange }) {
    const [comparator, setComparator] = useState('basicSalary');
    const [openRows, setOpenRows] = useState({});
    const [checkedIds, setCheckedIds] = useState(new Set());
    const [reviewNotes, setReviewNotes] = useState({});
    const [popoverState, setPopoverState] = useState({ open: false, anchorEl: null, employeeId: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [showOnlyWithReviewNote, setShowOnlyWithReviewNote] = useState(false);

    useEffect(() => {
        setCheckedIds(new Set(initialCheckedIds));
        setReviewNotes(initialNotes);
    }, [initialCheckedIds, initialNotes]);

    if (!payrollRun || !payrollRun1) return null;
    const usedCurrencyRates = payrollRun.usedCurrencyRates || { etb: 1 };
    const prevMap = getPayrollMap(payrollRun1.payrolls);
    const currMap = getPayrollMap(payrollRun.payrolls);
    // Find changed payrolls (by comparator field)
    const changes = [];
    let sumChanges = 0;
    (payrollRun.payrolls || []).forEach(curr => {
        const prev = prevMap[curr.employeeId];
        if (prev) {
            const currVal = curr[comparator];
            const prevVal = prev[comparator];
            if (currVal !== undefined && prevVal !== undefined && Math.abs(currVal - prevVal) > 0.01) {
                const diffs = comparePayrollItems(curr, prev, usedCurrencyRates);

               
                // console.log(diffs)
              if(diffs.length>0 && diffs.some(d=>d.field===comparator)){
                 let remarks = curr.remarks||[]
                if(prev.basicSalary !== prev.fullBasicSalary){
                    const remark = `💬 Last month payroll was prorated - ${prev.normalPayDays} Days`
                    remarks=[{text:remark}].concat(curr.remarks)

                }
                changes.push({
                    employee: { fullName: curr.fullName },
                    employeeId: curr.employeeId,
                    payrollId: curr.id,
                    currVal,
                    prevVal,
                    diff: currVal - prevVal,
                    diffs,
                    notes: curr.notes,
                    prevNotes: prev.notes,
                    remarks,
                    noteHistory: curr.employee.notes
                    
                });
                sumChanges += currVal - prevVal;
              }
            }
        }
    });
    // New payrolls
    const newPayrolls = (payrollRun.payrolls || []).filter(p => !prevMap[p.employeeId]);
    const sumNew = _.sumBy(newPayrolls, comparator);
    // Removed payrolls
    const removedPayrolls = (payrollRun1.payrolls || []).filter(p => !currMap[p.employeeId]);
    const sumRemoved = _.sumBy(removedPayrolls, comparator);
    // Total sum check
    const totalDiff = sumChanges + sumNew - sumRemoved;
    const diffColor = diff => diff > 0 ? green[700] : (diff < 0 ? red[700] : undefined);
    const handleExpandClick = (id) => {
        setOpenRows(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleCheck = (employeeId) => {
        const newCheckedIds = new Set(checkedIds);
        if (newCheckedIds.has(employeeId)) {
            newCheckedIds.delete(employeeId);
        } else {
            newCheckedIds.add(employeeId);
        }
        setCheckedIds(newCheckedIds);
        if (onReviewDataChange) {
            onReviewDataChange({
                checkedEmployeeIds: Array.from(newCheckedIds),
                payrollNotes: reviewNotes
            });
        }
    };

    const handleOpenPopover = (event, employeeId) => {
        setPopoverState({ open: true, anchorEl: event.currentTarget, employeeId });
    };

    const handleClosePopover = () => {
        setPopoverState({ open: false, anchorEl: null, employeeId: null });
    };

    const handleSaveNote = (note) => {
        const newReviewNotes = { ...reviewNotes, [popoverState.employeeId]: note };
        setReviewNotes(newReviewNotes);
        if (onReviewDataChange) {
            onReviewDataChange({
                checkedEmployeeIds: Array.from(checkedIds),
                payrollNotes: newReviewNotes
            });
        }
    };

    // Filtering logic for search and review note
    function filterRows(rows) {
        return rows.filter(function(row) {
            var matchesSearch = !searchTerm || (row.employee && row.employee.fullName && row.employee.fullName.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1);
            var hasReviewNote = reviewNotes[row.employeeId] && reviewNotes[row.employeeId].trim() !== '';
            if (showOnlyWithReviewNote) {
                return matchesSearch && hasReviewNote;
            }
            return matchesSearch;
        });
    }
    var filteredChanges = filterRows(changes);
    var filteredNewPayrolls = filterRows(newPayrolls);
    var filteredRemovedPayrolls = filterRows(removedPayrolls);

    // Check all logic
    function handleCheckAll(rows, checked) {
        var newCheckedIds = new Set(checkedIds);
        for (var i = 0; i < rows.length; i++) {
            var id = rows[i].employeeId;
            if (checked) {
                newCheckedIds.add(id);
            } else {
                newCheckedIds.delete(id);
            }
        }
        setCheckedIds(newCheckedIds);
        if (onReviewDataChange) {
            onReviewDataChange({
                checkedEmployeeIds: Array.from(newCheckedIds),
                payrollNotes: reviewNotes
            });
        }
    }
    function isAllChecked(rows) {
        if (!rows.length) return false;
        for (var i = 0; i < rows.length; i++) {
            var id = rows[i].employeeId;
            if (!checkedIds.has(id)) return false;
        }
        return true;
    }

    return (
        <Box mt={2}>
            {/* Search and filter controls */}
            <Box mb={2} display="flex" alignItems="center" style={{ gap: 16 }}>
                <TextField
                    label="Search Employee"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={function(e) { setSearchTerm(e.target.value); }}
                    style={{ minWidth: 200 }}
                />
                <FormControl variant="outlined" size="small">
                    <InputLabel id="comparator-label">Compare Field</InputLabel>
                    <Select
                        labelId="comparator-label"
                        value={comparator}
                        onChange={function(e) { setComparator(e.target.value); }}
                        label="Compare Field"
                        style={{ minWidth: 180 }}
                    >
                        {COMPARATOR_FIELDS.map(function(f) {
                            return <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>;
                        })}
                    </Select>
                </FormControl>
                <FormControl size="small" style={{ marginLeft: 16 }}>
                    <Checkbox
                        checked={showOnlyWithReviewNote}
                        onChange={function(e) { setShowOnlyWithReviewNote(e.target.checked); }}
                        color="primary"
                        style={{ padding: 0, marginRight: 8 }}
                    />
                    <span style={{ fontSize: 14 }}>Show Only With Review Note</span>
                </FormControl>
            </Box>
            <Typography variant="h6"> {filteredChanges.length} Changes :- {sumChanges} </Typography>
            <NotesPopover 
                open={popoverState.open} 
                anchorEl={popoverState.anchorEl} 
                onClose={handleClosePopover} 
                onSave={handleSaveNote}
                initialValue={reviewNotes[popoverState.employeeId] || ''}
            />
            <Paper style={{ marginBottom: 24, padding: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isAllChecked(filteredChanges)}
                                    onChange={function(e) { handleCheckAll(filteredChanges, e.target.checked); }}
                                />
                            </TableCell>
                            <TableCell>Employee</TableCell>
                            <TableCell>Previous</TableCell>
                            <TableCell>Current</TableCell>
                            <TableCell>Diff</TableCell>
                            <TableCell style={{ minWidth: 150 }}>Payroll Notes</TableCell>
                            <TableCell style={{ width: 120, maxWidth: 150 }}>Last Month Payroll Notes</TableCell>
                            <TableCell>System Remarks</TableCell>
                            <TableCell>Review Note</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredChanges.map(function(row) {
                            console.log({row})
                            return (
                                <React.Fragment key={row.employeeId}>
                                    <TableRow>
                                        <TableCell>
                                            <IconButton size="small" onClick={function() { handleExpandClick(row.employeeId); }}>
                                                {openRows[row.employeeId] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={checkedIds.has(row.employeeId)}
                                                onChange={function() { handleCheck(row.employeeId); }}
                                            />
                                        </TableCell>
                                        <TableCell>{row.employee.fullName}</TableCell>
                                        <TableCell>{row.prevVal}</TableCell>
                                        <TableCell>{row.currVal}</TableCell>
                                        <TableCell style={{ color: diffColor(row.diff) }}>{row.diff > 0 ? '+' : ''}{row.diff.toFixed(2)}</TableCell>
                                          <TableCell style={{ whiteSpace: 'pre-wrap', minWidth: 150 }}>
                                            {(row.notes && row.notes.length > 0) ? row.notes.map(function(n) { return n.note; }).join('\n') : ''}
                                        </TableCell>
                                               <TableCell style={{ whiteSpace: 'pre-wrap', width: 120, maxWidth: 150 }}>
                                            {(row.prevNotes && row.prevNotes.length > 0) ? row.prevNotes.map(function(n) { return n.note; }).join('\n') : ''}
                                        </TableCell>
                                              <TableCell style={{ whiteSpace: 'pre-wrap' }}>
                                            {(row.remarks && row.remarks.length > 0) ? row.remarks.map(function(n) { return n.text; }).join('\n') : ''}
                                        </TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={function(e) { handleOpenPopover(e, row.employeeId); }}>
                                                <RateReview color={reviewNotes[row.employeeId] ? "primary" : "inherit"} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                                            <Collapse in={openRows[row.employeeId]} timeout="auto" unmountOnExit>
                                                <Box margin={1}>
                                                    <Typography variant="subtitle2">Details</Typography>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Field</TableCell>
                                                                <TableCell>Previous</TableCell>
                                                                <TableCell>Current</TableCell>
                                                                <TableCell>Diff</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {row.diffs.map(function(diff, i) {
                                                                return (
                                                                    <TableRow key={diff.field}>
                                                                        <TableCell>{diff.field}</TableCell>
                                                                        <TableCell>{diff.prevVal}</TableCell>
                                                                        <TableCell>{diff.currVal}</TableCell>
                                                                        <TableCell style={{ color: diffColor(diff.diff) }}>{diff.diff > 0 ? '+' : ''}{diff.diff.toFixed(2)}</TableCell>
                                                                    </TableRow>
                                                                );
                                                            })}
                                                            {row.diffs.length === 0 && (
                                                                <TableRow><TableCell colSpan={4}>No details</TableCell></TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                    {row.noteHistory && row.noteHistory.length > 0 && (
                                                        <Box mt={1} style={{ fontStyle: 'italic', fontSize: 12, background: '#f9f9f9' }}>
                                                            Notes:
                                                            <ul style={{margin: 0, paddingLeft: 16}}>
                                                              {row.noteHistory.map(function(n, idx) {
                                                                return (
                                                                  <li key={idx}>
                                                                    <div><b>Note:</b> {n.note}</div>
                                                                    {n.noteBy && <div><b>By:</b> {n.noteBy}</div>}
                                                                    {n.noteDate && <div><b>Date:</b> {n.noteDate}</div>}
                                                                    {n.noteType && <div><b>Type:</b> {n.noteType}</div>}
                                                                  </li>
                                                                );
                                                              })}
                                                            </ul>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            );
                        })}
                        {filteredChanges.length === 0 && (
                            <TableRow><TableCell colSpan={10}>No changes</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
            <Typography variant="h6"> {filteredNewPayrolls.length}  New Payrolls :- {sumNew}</Typography>
            <Paper style={{ marginBottom: 24, padding: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isAllChecked(filteredNewPayrolls)}
                                    onChange={function(e) { handleCheckAll(filteredNewPayrolls, e.target.checked); }}
                                />
                            </TableCell>
                            <TableCell>Employee</TableCell>
                            <TableCell>{comparator}</TableCell>
                            <TableCell>Payroll Notes</TableCell>
                            <TableCell>Review Note</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredNewPayrolls.map(function(row) {
                            return (
                                <TableRow key={row.employeeId}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={checkedIds.has(row.employeeId)}
                                            onChange={function() { handleCheck(row.employeeId); }}
                                        />
                                    </TableCell>
                                    <TableCell>{row.fullName}</TableCell>
                                    <TableCell style={{ color: diffColor(row[comparator]) }}>+{row[comparator] && row[comparator].toFixed ? row[comparator].toFixed(2) : row[comparator]}</TableCell>
                                    <TableCell>
                                        {(row.notes && row.notes.length > 0) ? row.notes.map(function(n) { return n.note; }).join(', ') : ''}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={function(e) { handleOpenPopover(e, row.employeeId); }}>
                                            <RateReview color={reviewNotes[row.employeeId] ? "primary" : "inherit"}/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {filteredNewPayrolls.length === 0 && (
                            <TableRow><TableCell colSpan={5}>No new payrolls</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
            <Typography variant="h6"> {filteredRemovedPayrolls.length} Removed Payrolls :- {sumRemoved}</Typography>
            <Paper style={{ marginBottom: 24, padding: 0 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={isAllChecked(filteredRemovedPayrolls)}
                                    onChange={function(e) { handleCheckAll(filteredRemovedPayrolls, e.target.checked); }}
                                />
                            </TableCell>
                            <TableCell>Employee</TableCell>
                            <TableCell>{comparator}</TableCell>
                            <TableCell>Payroll Notes</TableCell>
                            <TableCell>Review Note</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRemovedPayrolls.map(function(row) {
                            return (
                                <TableRow key={row.employeeId}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={checkedIds.has(row.employeeId)}
                                            onChange={function() { handleCheck(row.employeeId); }}
                                        />
                                    </TableCell>
                                    <TableCell>{row.fullName}</TableCell>
                                    <TableCell style={{ color: diffColor(-row[comparator]) }}>-{row[comparator] && row[comparator].toFixed ? row[comparator].toFixed(2) : row[comparator]}</TableCell>
                                    <TableCell>
                                        {(row.notes && row.notes.length > 0) ? row.notes.map(function(n) { return n.note; }).join(', ') : ''}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={function(e) { handleOpenPopover(e, row.employeeId); }}>
                                            <RateReview color={reviewNotes[row.employeeId] ? "primary" : "inherit"} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {filteredRemovedPayrolls.length === 0 && (
                            <TableRow><TableCell colSpan={5}>No removed payrolls</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
            <Box mt={2}>
               <CaptionedField label="Total Diff (analysis)" value={totalDiff} color={diffColor(totalDiff)} />
               <CaptionedField label="Total Diff Payroll Runs" value={payrollRun.cash-payrollRun1.cash} color={diffColor(payrollRun.cash-payrollRun1.cash)} />
            </Box>
        </Box>
    );
}

export default PayrollComparisonTable; 